// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ButtonWrapper } from "@insite/mobius/Button";
import { IconWrapper } from "@insite/mobius/Icon";
import { actionStyles } from "@turnerBlueprint/Overrides/Widgets/MyListsDetails/MyListsDetailsActions";
import IconArrowDown from "@turnerBlueprint/XDL/Icons/IconArrowDown";
import IconFavoriteEmpty from "@turnerBlueprint/XDL/Icons/IconFavoriteEmpty";
import IconFavoriteFilled from "@turnerBlueprint/XDL/Icons/IconFavoriteFilled";
import { fontConfig } from "@turnerBlueprint/XDL/PreStyle/typography";
import { color, xScreen } from "@turnerBlueprint/XDL/Utilities";
import { css } from "styled-components";

actionStyles.wrapper = {
    css: css`
        display: flex;
        justify-content: space-between;
        gap: 22px;
        border-bottom: 1px solid ${color("common.border")};
        margin-bottom: 12px;
        padding-bottom: 12px;
        ${xScreen("lte", "md")} {
            flex-direction: column;
            gap: 16px;
        }
    `,
};

actionStyles.actionsWrapperRight = {
    css: css`
        display: flex;
        flex-direction: column;
        flex: 1;
        gap: 12px;
        max-width: 50%;
        ${xScreen("lte", "md")} {
            max-width: 100%;
            gap: 20px;
        }
    `,
};

actionStyles.buttonWrapper = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        gap: 15px;
        ${xScreen("lte", "md")} {
            align-items: center;
        }
        ${xScreen("lt", "sm")} {
            flex-direction: column;
            align-items: flex-start;
        }
    `,
};

actionStyles.listInfoWrapper = {
    css: css`
        display: flex;
        justify-content: flex-end;
        align-items: flex-start;
        gap: 76px;
        ${xScreen("lte", "sm")} {
            flex-wrap: wrap;
            justify-content: flex-start;
            align-items: flex-start;
        }

        ${xScreen("lte", "xs")} {
            width: 100%;
            gap: 16px;

            & > div {
                flex: 1;
            }
        }
    `,
};

actionStyles.innerButtonWrapper = {
    css: css`
        display: flex;
        gap: 24px;
        align-items: end;
        flex-wrap: wrap;
        ${xScreen("lte", "xs")} {
            width: 100%;
            gap: 16px;

            & > div {
                flex: 1;
            }

            ${ButtonWrapper} {
                flex: 1;
                width: 100%;
            }
        }
    `,
};

actionStyles.statusContainer = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        gap: 18px;
        margin-bottom: 16px;
        align-items: baseline;
        ${xScreen("lt", "sm")} {
            gap: 12px;
            margin-bottom: 12px;
        }
    `,
};

actionStyles.addItemButton = {
    typographyProps: {
        weight: "bold",
        css: css`
            white-space: nowrap;
        `,
    },
    css: css`
        width: 100%;
        padding: 8px 24px;
    `,
    variant: "secondary",
};
actionStyles.removeSelectedButton = {
    typographyProps: {
        weight: "bold",
        css: css`
            white-space: nowrap;
        `,
    },
    css: css`
        width: 100%;
        padding: 0 15px;
    `,
    variant: "secondary",
};
actionStyles.addListButton = {
    css: css`
        width: fit-content;
        padding: 0 15px;
        white-space: nowrap;

        ${xScreen("lte", "xs")} {
            padding: 0;
        }
    `,
};

actionStyles.editListModal = {
    sizeVariant: "small",
};
actionStyles.copyListModal = {
    sizeVariant: "small",
};

actionStyles.actionsWrapperLeft = {
    css: css`
        max-width: 50%;
        ${xScreen("lte", "md")} {
            max-width: none;
        }
    `,
};

actionStyles.listActionsWrapper = {
    css: css`
        margin-bottom: 20px;
        display: flex;
        flex-wrap: wrap;
        gap: 20px;
        ${xScreen("lte", "md")} {
            margin-bottom: 0;
        }
    `,
};

actionStyles.nameText = {
    variant: "h1",
    css: css`
        display: block;
        word-wrap: break-word;
        ${xScreen("lte", "md")} {
            max-width: 100%;
        }
    `,
};

actionStyles.wishlistCountWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        gap: 4px;
    `,
};

actionStyles.wishlistLabel = {
    size: 14,
    lineHeight: "22px",
};

actionStyles.wishlistCount = {
    size: 14,
    lineHeight: "22px",
    weight: "bold",
};

actionStyles.toggleFavoriteWrapper = {
    css: css`
        margin-left: 15px;
        font-weight: ${fontConfig.weight.bold};
        span {
            line-height: 14px;
        }
    `,
};

actionStyles.iconContainer = {
    css: css`
        width: fit-content;
        ${xScreen("lt", "sm")} {
            margin-left: 20px;
        }
    `,
};
actionStyles.actionFavoriteClickable = {
    css: css`
        font-size: 12px;
        color: ${color("text.link")};
        weight: ${fontConfig.weight.bold};
        width: fit-content;
    `,
};
actionStyles.starIcon = {
    src: IconFavoriteEmpty,
    size: 12,
    color: "text.link",
    css: css`
        margin-right: 8px;
    `,
};
actionStyles.starXIcon = {
    src: IconFavoriteFilled,
    color: "text.link",
    size: 12,
    css: css`
        margin-right: 8px;
    `,
};

actionStyles.overflowMenu = {
    _width: 140,
    cssOverrides: {
        wrapper: css`
            width: auto;
            height: auto;
            & button[aria-expanded="true"] ${IconWrapper} svg {
                transform: rotate(180deg);
            }
        `,
        menu: css`
            padding: 4px 0;
            li:last-child button {
                padding-bottom: 13px;
                &::after {
                    display: none;
                }
            }
            li:first-child button {
                padding-top: 13px;
            }
            @media print {
                display: none;
            }
        `,

        menuItem: css`
            padding: 8px 13px 0;
            font-size: 12px;
            line-height: 13px;
            color: ${color("text.link")};
            font-weight: bold;
            &::after {
                content: "";
                display: block;
                border-bottom: 1px solid ${color("common.border")};
                width: calc(100% - 12px);
                margin-top: 8px;
            }
        `,
    },
};

actionStyles.moreActions = {
    icon: {
        iconProps: {
            src: IconArrowDown,
            size: 12,
            css: css`
                &,
                & > svg {
                    width: 8px;
                }
            `,
        },
        position: "right",
    },
    typographyProps: {
        size: 12,
        lineHeight: "14px",
    },
    css: css`
        height: 14px;
    `,
};

const customClickable = {
    css: css`
        font-size: 12px;
        height: 14px;
        font-weight: ${fontConfig.weight.bold};
        color: ${color("text.link")};
        padding: 0;
        margin: 0;

        &:hover {
            color: ${color("common.ui9")};
        }
    `,
};
actionStyles.printButton = {
    ...customClickable,
};
actionStyles.addTagsButton = {
    ...customClickable,
};
actionStyles.scheduleButton = {
    ...customClickable,
};
actionStyles.editButton = {
    ...customClickable,
};
actionStyles.shareButton = {
    ...customClickable,
};
actionStyles.hideButton = {
    ...customClickable,
};
actionStyles.copyButton = {
    ...customClickable,
};
actionStyles.exportButton = {
    ...customClickable,
};
actionStyles.deleteButton = {
    ...customClickable,
};
